import { SmallIcon } from '@/components/Icon'
import Data from '@/data'
import { WithGemTooltip } from '@/tooltip'
import { CallbackWrap } from '@/types/common'
import { SkillPriorityGroup } from '@/types/embed'
import { Gem } from '@/types/item'
import { Icon, ScrollBox, TextInput } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import update from 'immutability-helper'
import React, { Fragment, useCallback } from 'react'
import { PlannerEditSkill } from '../skills/edit-skill'
import './skill-priority.scss'

function GemComponent({ gem }: { gem: Gem }) {
  const gemData = Data.gems[gem.id]

  return (
    <div className="poe-skill-gem">
      <WithGemTooltip gem={gem}>
        <div>{!!gemData.active_skill?.icon && <SmallIcon icon={gemData.active_skill.icon} />}</div>
      </WithGemTooltip>
    </div>
  )
}

function SkillGroup({ group, onClick }: { group: SkillPriorityGroup; onClick?: () => void }) {
  return (
    <div className={classNames('poe-skill-group', onClick ? 'poe-skill-group-editable' : undefined)} onClick={onClick}>
      <div className="poe-skill-gems">
        {group.gems.map((gem, index) => (
          <GemComponent key={index} gem={gem} />
        ))}
        {!group.gems.length && (
          <div className="poe-skill-gem">
            <div className="poe-icon poe-add-button" />
          </div>
        )}
      </div>
      <span className="poe-skill-group-label">{group.text}</span>
    </div>
  )
}

export function SkillPriorityComponent({
  title,
  setTitle,
  groups,
  setGroups,
  editMode
}: {
  title: string
  setTitle?: (value: string) => void
  groups: SkillPriorityGroup[] | undefined
  setGroups?: (value: SkillPriorityGroup[] | ((profile: SkillPriorityGroup[]) => SkillPriorityGroup[])) => void
  editMode: boolean
}) {
  const [editing, setEditing] = React.useState<number>()

  const onSkillGroupAdd = useCallback(() => {
    if (!setGroups) return
    setGroups((groups) => {
      const index = groups.length ?? 0
      groups = [...groups, { gems: [], text: '' }]
      return groups
    })
    setEditing(groups?.length ?? 0)
  }, [setGroups, groups?.length])

  const onPriorityEdit = useCallback(
    (value: CallbackWrap<SkillPriorityGroup | undefined>) => {
      if (editing == null || !setGroups) return

      setGroups((groups) => {
        const prev = groups[editing]
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return groups
        groups = update(groups, value ? { [editing]: { $set: value } } : { $splice: [[editing, 1]] })
        return groups
      })
    },
    [setGroups, editing]
  )

  if (!groups?.length && !editMode) return null
  return (
    <div className="poe-SkillPriority">
      <span className="poe-skill-prio-title">
        {setTitle ? <TextInput size="sm" value={title} onChange={setTitle} placeholder="Skill Rotation..." /> : title}
      </span>

      <ScrollBox className="poe-skill-prio-scrollbox" innerClassName="poe-skill-prio-inner" type="horizontal">
        {groups?.map((group, index) => (
          <Fragment key={index}>
            {index !== 0 ? (
              <div className="poe-skill-prio-sep">
                <Icon icon="chevron-right.svg" width={16} height={16} />
              </div>
            ) : null}
            <SkillGroup group={group} onClick={editMode ? () => setEditing(index) : undefined} />
          </Fragment>
        ))}

        {editMode && !!groups?.length && (
          <div className="poe-skill-prio-sep">
            <Icon icon="chevron-right.svg" width={16} height={16} />
          </div>
        )}
        {editMode && <SkillGroup group={{ gems: [], text: 'Add' }} onClick={onSkillGroupAdd} />}
      </ScrollBox>
      {editing != null && groups?.[editing] && (
        <PlannerEditSkill
          skill={groups[editing]!}
          onClose={() => setEditing(undefined)}
          onChange={onPriorityEdit}
          onlyActiveSkills={true}
          showSlotEditor={false}
        />
      )}
    </div>
  )
}
